<script setup>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { storeToRefs, getActivePinia, } from 'pinia'
import { computed, } from 'vue'
import { safeEncodeURI, } from '@/assets/js/src/util/safeEncodeURI'
import LoadingDummyH1 from '@/assets/js/src/components/loading/LoadingDummyH1'
import LoadingDummyP from '@/assets/js/src/components/loading/LoadingDummyP'
import { useBooknamesStore, } from '@/assets/js/src/modules/bible/_pinia/booknames'
import { useBibleStore, } from '@/assets/js/src/modules/bible/_pinia/bible'
import { excludeFootnotes, } from '@/assets/js/src/util/composables/useExcludeFootnotes'
import { useHeadInfo, } from '@/assets/js/src/modules/synoptic/_composables/useHeadInfo'
import { useTranslation, } from '@/assets/js/src/util/composables/useTranslation'
import { useSynopticApi, } from '@/assets/js/src/modules/synoptic/_composables/useSynopticApi'

const activePinia = getActivePinia()
const { synoptic,messagesLoaded,route, } = useSynopticApi()
const { booknamesLoaded,bible, } = useHeadInfo({ synoptic, })
const { t, } = useTranslation()
const { getDefaultBooknameByNumber, } = storeToRefs(useBooknamesStore(activePinia))
const { selectedBibles, } = storeToRefs(useBibleStore(activePinia))
const synopticFormatted = computed(()=>{
    let bibles = safeEncodeURI(selectedBibles.value.join('.'))

    return Object.entries(synoptic.value).map(({ 0:chapterCanonical,1:synopticContainer, }, index, containers)=>{
        let addClass
        if(0 === index) {
            addClass = 'first'
        } else  if((containers.length - 1) === index) {
            addClass = 'last'
        }

        let verses = synopticContainer.verses.map((verse)=>{
            return {
                id: verse.id,
                heading: verse.heading,
                verseNumber: verse.verse + (verse.verse_end ? `-${verse.verse_end}` : ''),
                content: excludeFootnotes(verse.content),
            }
        })

        let bookName
        if(booknamesLoaded.value) {
            bookName = getDefaultBooknameByNumber.value(Math.floor(chapterCanonical / 1000000))
        }

        return {
            addClass,
            chapterCanonical,
            verses,
            bookName,
            link: synopticContainer.link.replace(safeEncodeURI(route.params.abbreviation),bibles),
        }
    })
})
const cols = computed(()=>{
    let cols = Object.keys(synoptic.value).length
    let mdOuter = 2 * cols + 2

    return  {
        offsetMdOuter: (12 - mdOuter) / 2,
        mdOuter,
        mdInner: 12 / cols,
    }
})
</script>

<template>
    <overlay
        class-name="synoptic-view"
        full-width
    >
        <v-row>
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <h1
                    v-if="messagesLoaded"
                    class="hidden-print-only mb-4"
                >
                    {{ t('async.synoptic.title') }}
                </h1>
                <loading-dummy-h1
                    v-else
                >
                </loading-dummy-h1>
                <p v-if="messagesLoaded">
                    {{ t('async.synoptic.introduction', {bible: bible.name}) }}
                </p>
                <loading-dummy-p
                    v-else
                    count="1"
                >
                </loading-dummy-p>
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="12"
                :offset-md="cols.offsetMdOuter"
                :md="cols.mdOuter"
            >
                <v-row wrap>
                    <v-col
                        v-for="container in synopticFormatted"
                        :key="container.chapterCanonical"
                        cols="12"
                        :md="cols.mdInner"
                        :class="['synoptic-view__content__book', container.addClass,]"
                    >
                        <router-link :to="{path: container.link}">
                            <h2 v-if="booknamesLoaded">
                                {{ container.bookName }} {{ Math.floor(container.chapterCanonical / 1000) % 1000 }}
                            </h2>
                        </router-link>
                        <template
                            v-for="verse in container.verses"
                            :key="verse.id"
                        >
                            <h3 v-if="verse.heading">
                                {{ verse.heading }}
                            </h3>
                            <p>
                                <span class="synoptic-view__content__verse-number">{{ verse.verseNumber }}</span>&nbsp;
                                {{ verse.content }}
                            </p>
                        </template>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </overlay>
</template>

<style lang="scss">
.bs-app .synoptic-view {
    &__content {
        &__book {
            padding: 0 12px;

            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                padding: 0;
            }

            &.first {
                padding-left: 0;
            }

            &.last {
                padding-right: 0;
            }

            h2 {
                padding: 12px 0;
                font-weight: 400;
                font-size: 1.6em;
            }

            h3 {
                padding-bottom: 12px;
                font-weight: 300;
                font-size: 1.4em;
            }
        }

        &__verse-number {
            color: var(--bs-color-overlay-grey-dark);
            font-size: .8em;
        }
    }
}
</style>
